import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {LoadingButton} from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {ColumnDef} from '@tanstack/react-table';
import CountTextField from 'crqlar-react-common/dist/components/CountTextField';
import Property from 'crqlar-react-common/dist/components/Property';
import {Survey} from 'crqlar-react-common/dist/models/survey/survey.model';
import {SurveyTargetSet} from 'crqlar-react-common/dist/models/survey/surveyTargetSet.model';
import {SurveyService} from 'crqlar-react-common/dist/services/survey/survey.service';
import {useReadOnly} from 'crqlar-react-common/dist/utils/hooks/users/useReadOnly';
import {enqueueSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {useQueryClient} from 'react-query';

import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    serverTableQueryKey,
    useServerTable,
} from '../../../common/serverTable';
import type {ServerTableController} from '../../../common/serverTable';
import {MultiSelect} from '../../../common/newSelect';
import {
    SURVEY_BREAKDOWN_QUERY_KEY,
    SURVEY_INTRODUCTION_QUERY_KEY,
    SURVEY_SERIES_QUERY_KEY,
    SURVEY_SUMMARY_QUERY_KEY,
    SURVEY_TARGET_SETS_QUERY_KEY,
    SURVEY_TARGET_SETS_TABLE_ID,
} from '../../shared/surveyQueryRefresh';
import {useAllSurveys} from "../../shared/useAllSurveys";
import {
    SURVEY_CHART_TARGETS,
    SURVEY_TARGET_METRICS,
    SurveyChartTargetId,
    SurveyTargetMetricId,
} from '../data/surveyTargets.util';
import {useInsightsFormatters} from "../../../insights/kit/insightsFormat";

const SET_PAGE_SIZE = 999;
const SCREEN_HEIGHT = 560;

const MANAGER_TITLE_ID = 'target-sets-manager-title';

type SurveyTargetId = SurveyTargetMetricId | SurveyChartTargetId;

interface TargetField {
    id: SurveyTargetId;
    labelKey: string;
    unitKey?: string;
    min: number;
    max: number;
}

const BAND_FIELDS: TargetField[] = SURVEY_TARGET_METRICS;
const CHART_FIELDS: TargetField[] = SURVEY_CHART_TARGETS;
const TARGET_FIELDS: TargetField[] = [...BAND_FIELDS, ...CHART_FIELDS];

interface TargetSetDraft {
    id?: string;
    name: string;
    description: string;
    surveyIds: string[];
    values: Partial<Record<SurveyTargetId, number>>;
}

const draftOf = (set: SurveyTargetSet): TargetSetDraft => ({
    id: set.id,
    name: set.name ?? '',
    description: set.description ?? '',
    surveyIds: set.surveyIds ?? [],
    values: TARGET_FIELDS.reduce((values, field) => {
        const stored = set.values?.[field.id];
        if (stored != null) values[field.id] = stored;
        return values;
    }, {} as TargetSetDraft['values']),
});

const newDraft = (surveyIds: string[]): TargetSetDraft => ({name: '', description: '', surveyIds, values: {}});

const snack = (message: string, variant: 'success' | 'error') =>
    enqueueSnackbar(message, {variant, anchorOrigin: {vertical: 'bottom', horizontal: 'center'}});

interface SurveyOption {
    id: string;
    name: string;
}

interface ManagerHeaderProps {
    title: string;
    onBack?: () => void;
    onClose: () => void;
    disabled?: boolean;
}

function ManagerHeader({title, onBack, onClose, disabled}: ManagerHeaderProps) {
    const {t} = useTranslation();

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, minHeight: 56}}>
            {onBack != null && (
                <IconButton aria-label={t('Back')} onClick={onBack} disabled={disabled}>
                    <ArrowBackIcon/>
                </IconButton>
            )}

            <Typography id={MANAGER_TITLE_ID} component={'h2'} variant={'h6'} noWrap sx={{pl: onBack == null ? 1 : 0}}>
                {title}
            </Typography>

            <IconButton aria-label={t('Close')} onClick={onClose} disabled={disabled} sx={{ml: 'auto'}}>
                <CloseIcon/>
            </IconButton>
        </Box>
    );
}

interface TargetSetRecordScreenProps {
    draft: TargetSetDraft;
    surveys: SurveyOption[];
    onChange: (draft: TargetSetDraft) => void;
    onBack: () => void;
    onClose: () => void;
    onSave: () => void;
    onDelete: () => void;
    saving: boolean;
    readOnly: boolean;
}

function TargetSetRecordScreen({draft, surveys, onChange, onBack, onClose, onSave, onDelete, saving, readOnly}: TargetSetRecordScreenProps) {
    const {t} = useTranslation();

    const isNew = draft.id == null;

    const setValue = (field: TargetField, value: number | undefined) =>
        onChange({
            ...draft,
            values: {
                ...draft.values,
                [field.id]: value === undefined || isNaN(value) ? undefined : Math.min(Math.max(value, field.min), field.max),
            },
        });

    const selectedSurveys = surveys.filter(survey => draft.surveyIds.includes(survey.id));

    const targetRow = (field: TargetField) => (
        <Property
            key={field.id}
            title={field.unitKey == null ? t(field.labelKey) : `${t(field.labelKey)} (${t(field.unitKey)})`}
        >
            <CountTextField
                id={`target-set-${field.id}`}
                size={'small'}
                value={draft.values[field.id] ?? ''}
                onChangeValue={value => setValue(field, value)}
                InputProps={{inputProps: {min: field.min, max: field.max}}}
                disabled={saving || readOnly}
                sx={{width: 168}}
            />
        </Property>
    );

    return (
        <React.Fragment>
            <ManagerHeader
                title={isNew ? t('New target set') : draft.name.trim() || t('Edit target set')}
                onBack={onBack}
                onClose={onClose}
                disabled={saving}
            />

            <DialogContent dividers sx={{p: 3, height: SCREEN_HEIGHT}}>
                <Stack gap={1}>
                    <Property title={t('Name')}>
                        <TextField
                            id={'target-set-name'}
                            size={'small'}
                            value={draft.name}
                            onChange={event => onChange({...draft, name: event.target.value})}
                            disabled={saving || readOnly}
                            fullWidth
                            autoFocus
                        />
                    </Property>

                    <Property title={t('Description')} labelAlignment={'top'}>
                        <TextField
                            id={'target-set-description'}
                            size={'small'}
                            value={draft.description}
                            onChange={event => onChange({...draft, description: event.target.value})}
                            disabled={saving || readOnly}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                    </Property>

                    <Property
                        title={t('Applies to')}
                        tooltip={t('The surveys judged against this set. A survey uses one set at a time, so picking it here takes it away from the set it used before.')}
                        labelAlignment={'top'}
                    >
                        <MultiSelect
                            label={''}
                            placeholder={t('No survey yet')}
                            value={selectedSurveys.map(survey => survey.id)}
                            options={surveys.map(survey => ({value: survey.id, label: survey.name}))}
                            onChange={surveyIds => onChange({...draft, surveyIds})}
                            disabled={saving || readOnly}
                        />
                    </Property>

                    <Divider sx={{my: 1.5}}/>

                    <Typography variant={'body2'} color={'text.secondary'} sx={{mb: 0.5}}>
                        {t('A target shows up under its card on the Insights band and is judged against the period on screen. Leave a field empty to set no target. Time targets are met when guests need less time than them.')}
                    </Typography>

                    {BAND_FIELDS.map(targetRow)}

                    <Divider sx={{my: 1.5}}/>

                    <Typography variant={'body2'} color={'text.secondary'} sx={{mb: 0.5}}>
                        {t('Responses per day is judged day by day, completion on the whole period against everyone who started.')}
                    </Typography>

                    {CHART_FIELDS.map(targetRow)}
                </Stack>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                {!isNew && !readOnly && (
                    <Button color={'error'} onClick={onDelete} disabled={saving} sx={{mr: 'auto'}}>
                        {t('Delete')}
                    </Button>
                )}

                <Button color={'inherit'} onClick={onBack} disabled={saving}>
                    {t('Cancel')}
                </Button>

                <LoadingButton
                    variant={'contained'}
                    onClick={onSave}
                    loading={saving}
                    disabled={readOnly || draft.name.trim().length === 0}
                >
                    {isNew ? t('Create target set') : t('Save changes')}
                </LoadingButton>
            </DialogActions>
        </React.Fragment>
    );
}

interface TargetSetIndexScreenProps {
    controller: ServerTableController<SurveyTargetSet>;
    survey?: Survey;
    activeTargetSetId?: string | null;
    onClearActive?: () => void;
    assigning: boolean;
    onCreate: () => void;
    onClose: () => void;
    readOnly: boolean;
}

function TargetSetIndexScreen({controller, survey, activeTargetSetId, onClearActive, assigning, onCreate, onClose, readOnly}: TargetSetIndexScreenProps) {
    const {t} = useTranslation();

    const switching = survey != null && onClearActive != null;

    return (
        <React.Fragment>
            <ManagerHeader title={t('Manage target sets')} onClose={onClose}/>

            <DialogContent dividers sx={{p: 2, height: SCREEN_HEIGHT, display: 'flex', flexDirection: 'column', gap: 1}}>
                {survey != null && (
                    <Typography variant={'body2'} color={'text.secondary'} sx={{px: 1}}>
                        {t('The checked set is active for {{survey}}. Uncheck it to judge the survey against nothing.', {survey: survey.name || survey.id})}
                    </Typography>
                )}

                <Box sx={{flex: 1, minHeight: 0}}>
                    <ServerTableProvider controller={controller}>
                        <ServerTableFrame>
                            <ServerTable showPagination={false}/>
                        </ServerTableFrame>
                    </ServerTableProvider>
                </Box>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                {switching && !readOnly && !!activeTargetSetId && (
                    <Button color={'inherit'} onClick={onClearActive} disabled={assigning} sx={{mr: 'auto'}}>
                        {t('Use no targets')}
                    </Button>
                )}

                <Button color={'inherit'} onClick={onClose} sx={{ml: 'auto'}}>
                    {t('Close')}
                </Button>

                {!readOnly && (
                    <Button variant={'contained'} startIcon={<AddIcon/>} onClick={onCreate}>
                        {t('New target set')}
                    </Button>
                )}
            </DialogActions>
        </React.Fragment>
    );
}

interface DeleteTargetSetDialogProps {
    setName: string;
    surveyCount: number;
    onCancel: () => void;
    onConfirm: () => void;
    saving: boolean;
}

function DeleteTargetSetDialog({setName, surveyCount, onCancel, onConfirm, saving}: DeleteTargetSetDialogProps) {
    const {t} = useTranslation();

    return (
        <Dialog open onClose={() => !saving && onCancel()} fullWidth maxWidth={'sm'}>
            <DialogTitle>{t('Delete target set?')}</DialogTitle>

            <DialogContent>
                <Alert severity={'warning'}>
                    {surveyCount > 0
                        ? t('{{name}} is used by {{count}} surveys. They lose their targets: no target bars on their Insights band and no verdict colours on their charts until they are given another set.', {name: setName, count: surveyCount})
                        : t('{{name}} is used by no survey and is deleted for good.', {name: setName})}
                </Alert>
            </DialogContent>

            <DialogActions>
                <Button color={'inherit'} onClick={onCancel} disabled={saving}>
                    {t('Cancel')}
                </Button>

                <LoadingButton color={'error'} variant={'contained'} onClick={onConfirm} loading={saving}>
                    {t('Delete')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

interface TargetSetsManagerDialogProps {
    open: boolean;
    onClose: () => void;
    survey?: Survey;
    /** The set whose `surveyIds` list the survey; the survey itself has no target field. */
    activeTargetSetId?: string | null;
    onAssign?: (setId: string | null) => Promise<void>;
}

export function TargetSetsManagerDialog({open, onClose, survey, activeTargetSetId, onAssign}: TargetSetsManagerDialogProps) {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const readOnly = useReadOnly();

    const [draft, setDraft] = React.useState<TargetSetDraft | null>(null);
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [assigning, setAssigning] = React.useState(false);

    const switching = survey != null && onAssign != null;

    const assign = async (setId: string | null) => {
        if (onAssign == null || setId === (activeTargetSetId ?? null)) return;

        setAssigning(true);
        try {
            await onAssign(setId);
        } catch (e) {
            console.log(e);
            snack(t('Changes could not be saved. Check all fields!'), 'error');
        } finally {
            setAssigning(false);
        }
    };

    // Every survey, not a first page: "Applies to" is saved whole, so a survey missing here would be taken off the set.
    const {surveys: allSurveys} = useAllSurveys();
    const surveys = React.useMemo<SurveyOption[]>(
        () => allSurveys.map(survey => ({id: survey.id, name: survey.name || survey.id})),
        [allSurveys]
    );

    const {count: formatCount} = useInsightsFormatters();

    const columns = React.useMemo<ColumnDef<SurveyTargetSet>[]>(() => [
        {
            id: 'active',
            header: switching ? t('Active') : '',
            cell: ({row}) => {
                if (!switching) return null;

                const active = activeTargetSetId != null && row.original.id === activeTargetSetId;

                return (
                    <Box data-no-row-click onClick={event => event.stopPropagation()} sx={{display: 'flex'}}>
                        <Checkbox
                            checked={active}
                            onChange={() => assign(active ? null : row.original.id ?? null)}
                            disabled={assigning || readOnly}
                            inputProps={{'aria-label': `${t('Use for {{survey}}', {survey: survey.name || survey.id})}: ${row.original.name ?? ''}`}}
                            sx={{p: 0.5}}
                        />
                    </Box>
                );
            },
            size: 72
        },
        {
            id: 'name',
            header: t('Target set'),
            accessorFn: set => set.name,
            cell: ({row}) => (
                <Typography variant={'body1'} noWrap>
                    {row.original.name || row.original.id}
                </Typography>
            ),
            size: 320,
            meta: {grow: true, isServerSortable: true}
        },
        {
            id: 'surveys',
            header: t('Surveys'),
            accessorFn: set => set.surveyIds?.length ?? 0,
            cell: ({row}) => (
                <Typography variant={'body1'} color={'text.secondary'}>
                    {formatCount(row.original.surveyIds?.length ?? 0)}
                </Typography>
            ),
            size: 120
        },
        {
            id: 'actions',
            header: '',
            cell: ({row}) => (
                <Stack direction={'row'} justifyContent={'flex-end'} data-no-row-click>
                    <Tooltip arrow title={readOnly ? t('View target set') : t('Edit target set')}>
                        <IconButton
                            size={'small'}
                            aria-label={`${readOnly ? t('View target set') : t('Edit target set')}: ${row.original.name ?? ''}`}
                            onClick={() => setDraft(draftOf(row.original))}
                        >
                            <EditOutlinedIcon fontSize={'small'}/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
            size: 72,
            meta: {align: 'right'}
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [activeTargetSetId, assigning, formatCount, readOnly, survey?.id, survey?.name, switching, t]);

    const controller = useServerTable<SurveyTargetSet>({
        tableId: SURVEY_TARGET_SETS_TABLE_ID,
        presentation: 'embedded',
        defaultPageSize: SET_PAGE_SIZE,
        columns,
        getRowId: set => set.id!,
        onRowClick: set => setDraft(draftOf(set)),
        initialSorting: [{id: 'name', desc: false}],
        enabled: open,
        queryFn: async ({pagination, sorting}) => {
            const page = await SurveyService.listTargetSets({
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    const wasOpen = React.useRef(false);

    React.useEffect(() => {
        if (open && !wasOpen.current) {
            setDraft(null);
            setConfirmDelete(false);
        }

        wasOpen.current = open;
    }, [open]);

    const closeDialog = () => {
        if (saving) return;

        setDraft(null);
        setConfirmDelete(false);
        onClose();
    };

    const refresh = () =>
        Promise.all([
            queryClient.invalidateQueries(serverTableQueryKey(SURVEY_TARGET_SETS_TABLE_ID)),
            queryClient.invalidateQueries([SURVEY_TARGET_SETS_QUERY_KEY]),
            queryClient.invalidateQueries([SURVEY_INTRODUCTION_QUERY_KEY]),
            queryClient.invalidateQueries([SURVEY_SERIES_QUERY_KEY]),
            queryClient.invalidateQueries([SURVEY_BREAKDOWN_QUERY_KEY]),
            queryClient.invalidateQueries([SURVEY_SUMMARY_QUERY_KEY]),
        ]);

    const save = async () => {
        if (draft == null || draft.name.trim().length === 0) return;

        const values = TARGET_FIELDS.reduce<Record<string, number>>((all, field) => {
            const value = draft.values[field.id];
            if (value != null) all[field.id] = value;
            return all;
        }, {});

        const changes = {name: draft.name.trim(), description: draft.description.trim(), values};

        setSaving(true);
        try {
            const saved = draft.id != null
                ? await SurveyService.updateTargetSet({...changes, id: draft.id})
                : await SurveyService.addTargetSet(changes);

            await SurveyService.assignTargetSet({id: saved.id!, surveyIds: draft.surveyIds});
            await refresh();
            setDraft(null);
            snack(t('Changes saved!'), 'success');
        } catch (e) {
            console.log(e);
            snack(t('Changes could not be saved. Check all fields!'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const deleteSet = async () => {
        if (draft?.id == null) return;

        setSaving(true);
        try {
            await SurveyService.deleteTargetSets({id: [draft.id]});
            await refresh();
            setConfirmDelete(false);
            setDraft(null);
            snack(t('Target set deleted'), 'success');
        } catch (e) {
            console.log(e);
            snack(t('Changes could not be saved. Check all fields!'), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <React.Fragment>
            <Dialog open={open} onClose={closeDialog} fullWidth maxWidth={'md'} aria-labelledby={MANAGER_TITLE_ID}>
                {draft != null ? (
                    <TargetSetRecordScreen
                        draft={draft}
                        surveys={surveys}
                        onChange={setDraft}
                        onBack={() => !saving && setDraft(null)}
                        onClose={closeDialog}
                        onSave={save}
                        onDelete={() => setConfirmDelete(true)}
                        saving={saving}
                        readOnly={readOnly}
                    />
                ) : (
                    <TargetSetIndexScreen
                        controller={controller}
                        survey={switching ? survey : undefined}
                        activeTargetSetId={activeTargetSetId}
                        onClearActive={switching ? () => assign(null) : undefined}
                        assigning={assigning}
                        onCreate={() => setDraft(newDraft(switching && survey.id ? [survey.id] : []))}
                        onClose={closeDialog}
                        readOnly={readOnly}
                    />
                )}
            </Dialog>

            {confirmDelete && draft != null && (
                <DeleteTargetSetDialog
                    setName={draft.name.trim() || t('This target set')}
                    surveyCount={draft.surveyIds.length}
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={deleteSet}
                    saving={saving}
                />
            )}
        </React.Fragment>
    );
}
